import { createLogger } from "utils/logging";
import { fetchRetry } from "utils/fetch";
import { DetailviewService } from "./DetailviewService";
import { TrackingService } from "./tracking/TrackingService";
import { captureErrorWithPayload } from "utils/error";
import { BenefitElement, defineElement, buildSearchParams, buildFetchHeaders } from "../benefit_base";

const ATTRIBUTE_NAMES = {
	VARIATION_ID: "variation-id",
	PRODUCT_ID: "product-id",
	EVENT_MERGE_ID: "event-merge-id",
	FEATURE: "feature",
	TEAM: "team",
	IS_PREFETCHED: "is-prefetched",
};

const searchParamBenefitId = "benefitId";
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const log = createLogger("tag", "detailview.component");
const DETAILVIEW_ELEMENT_NAME = "ft9-detailview-v1";

const ERROR_KEY = "ft9-benefit-detailview-not-ok";
const ERROR_LABELS = { service: "tag", module: "detailview", team: "ft9" };

async function callBackend(url: URL, headers: HeadersInit): Promise<Response> {
	const urlSearchParams = url.searchParams.toString();

	try {
		const response = await fetchRetry({
			url: url.toString(),
			namespace: "detailview",
			fetchOptions: {
				headers,
				priority: "high",
			},
			retryOptions: {
				limit: 2,
				delay: 50,
			},
		});

		if (!response.ok) {
			log.error("detailview endpoint returns a non ok response!", response.status);
			captureErrorWithPayload({
				message: "callBackend error",
				type: ERROR_KEY,
				labels: ERROR_LABELS,
				custom: { url_search_params: urlSearchParams, tries: 1 },
				payload: response.status,
			});
			return Promise.reject();
		}
		return response;
	} catch (error) {
		log.error("detailview request returned an error!", error);
		captureErrorWithPayload({
			message: "callBackend error",
			type: ERROR_KEY,
			labels: ERROR_LABELS,
			custom: { url_search_params: urlSearchParams, tries: 3 },
			payload: error,
		});
		return Promise.reject();
	}
}

export class DetailviewElement extends BenefitElement {
	private readonly detailviewService: DetailviewService;
	private autoActivateBenefitId: string | undefined;

	constructor() {
		super(); /*                                     */
		this.detailviewService = new DetailviewService(new TrackingService());
	}

	getAutoactiveBenefitId() {
		/*                                              */
		return this.autoActivateBenefitId;
	}

	protected async hydrate(): Promise<void> {
		if (this.getAttribute(ATTRIBUTE_NAMES.IS_PREFETCHED) === "true") {
			log.info("Detailview is prefetched, skipping backend call.");
			this.initDetailview();
			return;
		}

		this.replaceChildren(); /*                       */

		const variationId = this.getAttribute(ATTRIBUTE_NAMES.VARIATION_ID);
		if (!variationId) {
			log.error("No variationId provided, cannot load detailview.");
			captureErrorWithPayload({ message: "No variationId provided", type: ERROR_KEY, labels: ERROR_LABELS });
			return;
		}

		const benefitId = new URLSearchParams(window.location.search).get(searchParamBenefitId);
		if (benefitId && UUID_REGEX.test(benefitId)) {
			this.autoActivateBenefitId = benefitId;
		}

		const params = {
			variationId: this.getAttribute(ATTRIBUTE_NAMES.VARIATION_ID),
			productId: this.getAttribute(ATTRIBUTE_NAMES.PRODUCT_ID),
			benefitId: this.autoActivateBenefitId,
		};
		const urlSearchParams = buildSearchParams(params);
		log.info("Calling tag for %s with %s", this.id || "id-not-set", urlSearchParams.toString());

		const url = new URL(`/benefit-tag/detailview?${urlSearchParams}`, globalThis.location.origin);
		try {
			const headers = buildFetchHeaders(this.getAttribute(ATTRIBUTE_NAMES.FEATURE), this.getAttribute(ATTRIBUTE_NAMES.TEAM));
			const response = await callBackend(url, headers);

			if (response.status === 200) {
				this.innerHTML = await response.text();
				this.initDetailview();
				return;
			}

			this.detailviewService.handleNoContentDetailview(variationId, this);
		} catch {
			/*                                                                                    */
		}
	}

	private initDetailview() {
		const variationId = this.getAttribute(ATTRIBUTE_NAMES.VARIATION_ID);
		const eventMergeId = this.getAttribute(ATTRIBUTE_NAMES.EVENT_MERGE_ID);

		if (!variationId) {
			return;
		}

		this.detailviewService.initialize(variationId, this, eventMergeId);
	}
}

defineElement(DetailviewElement, DETAILVIEW_ELEMENT_NAME, "ftnine_emergency_custom_element_disable_detailview");
